import * as React from "react";

import { ScrollArea } from "@/components/ui/scroll-area";
import { TaskCardSmall } from "@/components/Planning/components/TaskCardSmall";
import { compareSameDate } from "@/utils/taskLogic";

const MAX_DATE_VALUE = 8640000000000000;
// Cards shown at first and added by "Show more" (there can be thousands with "Mine only" off)
const TASKS_PER_PAGE = 60;

/**
 * The dashboard's task cards: the rows it is given (already filtered), by due date - overdue first,
 * then upcoming, then those without a date - shown TASKS_PER_PAGE at a time.
 */
export function TaskList({ trials, onTaskComplete, currentUserId, showAssignee = false, emptyText = "No open tasks" }) {
  const [shown, setShown] = React.useState(TASKS_PER_PAGE);
  // A new selection starts at the top again
  const rowsKey = trials.map((t) => t._rowKey || t.id).join(",");
  React.useEffect(() => { setShown(TASKS_PER_PAGE); }, [rowsKey]);

  const sorted = React.useMemo(() => [...trials].sort((a, b) => {
    const dateA = a.task?.dueDate ? new Date(a.task.dueDate) : new Date(MAX_DATE_VALUE);
    const dateB = b.task?.dueDate ? new Date(b.task.dueDate) : new Date(MAX_DATE_VALUE);
    // Same date: by trial, then the trial's own order (number, then creation)
    return dateA - dateB
      || String(a.trialno ?? "").localeCompare(String(b.trialno ?? ""))
      || compareSameDate(a.task, b.task);
  }), [trials]);

  // Fill parent height and let ScrollArea handle scrolling
  return (
    <div className="flex flex-col h-full min-h-0">
      {sorted.length === 0 ? (
        <div className="flex items-center justify-center h-32 text-gray-500">{emptyText}</div>
      ) : (
        <ScrollArea className="flex-1 min-h-0">
          {/* Equal columns and equal row heights: every card the same size */}
          <div className="grid auto-rows-fr grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2 p-1">
            {sorted.slice(0, shown).map((trial) => (
              <div key={trial._rowKey || trial.id} className="h-full min-w-0">
                <TaskCardSmall
                  trial={trial}
                  task={trial.task}
                  onTaskComplete={onTaskComplete}
                  onTaskCancel={onTaskComplete}
                  currentUserId={currentUserId}
                  showAssignee={showAssignee}
                />
              </div>
            ))}
          </div>
          {sorted.length > shown && (
            <div className="flex justify-center p-2">
              <button
                type="button"
                onClick={() => setShown((n) => n + TASKS_PER_PAGE)}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
              >
                Show more ({sorted.length - shown} more)
              </button>
            </div>
          )}
        </ScrollArea>
      )}
    </div>
  );
}
